import { BaseFieldItem } from '../../store/models/field-models';
import { isAsyncCustomRule, isCustomRule, ValidationRuleType } from '../../store/models/validator-models';

type TranslationParamValue = string | number | boolean;
type TranslationParams = Record<string, TranslationParamValue>;

interface FieldErrorControl {
    errors: Record<string, unknown> | null;
}

interface ErrorMessageTranslator {
    instant(key: string, interpolateParams?: TranslationParams): string;
}

interface ParameterizedTranslationMessage {
    key: string;
    params: TranslationParams;
}

/**
 * Extracts error messages from a form control based on validation rules
 * @param control The form control to extract errors from
 * @param field The field configuration containing validators and error messages
 * @returns Array of error messages to display
 */
export function extractFieldErrorMessages(
    control: FieldErrorControl,
    field: BaseFieldItem,
    translator?: ErrorMessageTranslator,
): string[] {
    if (!control?.errors || !field?.validators) {
        return [];
    }

    const messages: string[] = [];

    Object.keys(control.errors).forEach((errorKey) => {
        if (field.errors && field.errors[errorKey]) {
            messages.push(field.errors[errorKey]);
        }

        if (!field.validators) {
            return;
        }

        const regularValidator = field.validators.find((v) => v.type.toLowerCase() === errorKey.toLowerCase());
        const customValidator = field.validators.find((v) => isCustomRule(v) && v.identifier === errorKey);
        const customAsyncValidator = field.validators.find((v) => isAsyncCustomRule(v) && v.identifier === errorKey);

        addMessages(messages, regularValidator?.message);
        addMessages(messages, customValidator?.message);
        addMessages(messages, customAsyncValidator?.message);
    });

    return getUniqueMessages(messages, translator);
}

function addMessages(messages: string[], message?: string | string[]): void {
    if (!message) {
        return;
    }

    if (typeof message === 'string') {
        messages.push(message);
        return;
    }

    messages.push(...message);
}

function getUniqueMessages(messages: string[], translator?: ErrorMessageTranslator): string[] {
    const displayedMessages = new Set<string>();

    return messages.filter((message) => {
        const displayMessage = translator ? translateMessage(message, translator) : message;

        if (displayedMessages.has(displayMessage)) {
            return false;
        }

        displayedMessages.add(displayMessage);
        return true;
    });
}

function translateMessage(message: string, translator: ErrorMessageTranslator): string {
    if (message.startsWith('{') && message.includes('"key"')) {
        try {
            const data: unknown = JSON.parse(message);
            if (isParameterizedTranslationMessage(data)) {
                return translator.instant(data.key, data.params);
            }
        } catch {
            return translator.instant(message);
        }
    }

    return translator.instant(message);
}

function isParameterizedTranslationMessage(value: unknown): value is ParameterizedTranslationMessage {
    if (!isObjectRecord(value)) {
        return false;
    }

    return typeof value.key === 'string' && isTranslationParams(value.params);
}

function isTranslationParams(value: unknown): value is TranslationParams {
    if (!isObjectRecord(value)) {
        return false;
    }

    return Object.values(value).every(isTranslationParamValue);
}

function isTranslationParamValue(value: unknown): value is TranslationParamValue {
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
